import { useState, useCallback, useRef, memo } from 'react';
import { Menu, MenuItem, Tooltip, Typography, Box, Divider, Badge, Button } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ptzIconInactive from 'assets/ptz-icon-inactive.svg';
import ptzIconActive from 'assets/ptz-icon-active.svg';
import { PTZ_REQUEST } from './config';
import { STREAM_FOOTER_ICON_STYLE } from 'components/transaction/components/panels/transaction/cameras/MediaSection/constants';

const KEYBOARD_SHORTCUT_STYLE = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    padding: '2px 6px',
    borderRadius: '4px',
    borderTop: '0.5px solid rgba(100, 101, 113, 0.3)',
    borderRight: '0.5px solid rgba(100, 101, 113, 0.3)',
    borderBottom: '2px solid rgba(100, 101, 113, 0.3)',
    borderLeft: '0.5px solid rgba(100, 101, 113, 0.3)',
    backgroundColor: '#fff',
    fontSize: '10px',
    fontWeight: 500,
    color: 'rgba(0, 0, 0, 0.6)',
    lineHeight: 1,
    marginLeft: '16px',
};

const MENU_SLOT_PROPS = {
    paper: {
        sx: {
            minWidth: '200px',
            borderRadius: '4px',
            boxShadow: '0 3px 14px 2px rgba(0, 0, 0, 0.06), 0 8px 10px 1px rgba(0, 0, 0, 0.06), 0 5px 5px -3px rgba(0, 0, 0, 0.06)',
            zIndex: 10002,
        },
    },
};

const MENU_ITEM_ROW_SX = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' };

function PTZDropdownButton({ isPTZOn, togglePTZ, fullZoomIn, fullZoomOut, ptzRequestStatus, isCameraOutOfFocus }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const buttonRef = useRef(null);
    const isMenuOpen = Boolean(anchorEl);
    const isRequestInProgress = ptzRequestStatus?.status === PTZ_REQUEST.STATUS.IN_PROGRESS;

    const handleButtonClick = useCallback(
        (e) => {
            if (!isPTZOn) {
                togglePTZ();
                return;
            }
            setAnchorEl(e.currentTarget);
        },
        [isPTZOn, togglePTZ]
    );

    const handleMenuClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const handleFullZoomIn = useCallback(() => {
        setAnchorEl(null);
        fullZoomIn();
    }, [fullZoomIn]);

    const handleFullZoomOut = useCallback(() => {
        setAnchorEl(null);
        fullZoomOut();
    }, [fullZoomOut]);

    const handleExitPTZ = useCallback(() => {
        setAnchorEl(null);
        togglePTZ();
    }, [togglePTZ]);

    const getTooltipTitle = () => {
        if (!isPTZOn) return 'PTZ Control';
        if (isCameraOutOfFocus) return 'Presets are disabled while camera is out of focus';
        return 'Drag ↘ to zoom in, ↖ to zoom out. Press 1-9 to different PTZ presets.(may take over 2s)';
    };

    return (
        <Tooltip title={getTooltipTitle()} placement="top">
            <Button
                ref={buttonRef}
                onClick={handleButtonClick}
                size="small"
                color="secondary"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    backgroundColor: isPTZOn ? '#fff' : 'transparent',
                    padding: '0px',
                    borderRadius: '0px',
                    '&:hover': {
                        backgroundColor: isPTZOn ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:focus': { outline: 'none' },
                    '&.MuiButton-root': {
                        padding: '2px 4px',
                        minWidth: '0px',
                        height: 'var(--camera-header-height) or var(--expanded-camera-header-height)',
                    },
                }}
            >
                <Badge
                    variant="dot"
                    color="warning"
                    invisible={!isCameraOutOfFocus}
                    sx={{
                        '& .MuiBadge-badge': {
                            backgroundColor: '#ff9800',
                            boxShadow: '0 0 0 2px #fff',
                        },
                    }}
                >
                    <img src={isPTZOn ? ptzIconActive : ptzIconInactive} alt="PTZ" style={STREAM_FOOTER_ICON_STYLE} />
                </Badge>
                {isPTZOn && (
                    <ArrowDropDownIcon
                        sx={{
                            fontSize: '18px',
                            marginLeft: '-4px',
                            transition: 'transform 0.2s ease',
                            transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            color: '#1976d2',
                        }}
                    />
                )}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                slotProps={MENU_SLOT_PROPS}
                sx={{ zIndex: 10002 }}
                disableScrollLock={true}
            >
                <MenuItem onClick={handleFullZoomIn} disabled={isRequestInProgress}>
                    <Box sx={MENU_ITEM_ROW_SX}>
                        <Typography variant="body2">Zoom fully IN</Typography>
                        <span style={KEYBOARD_SHORTCUT_STYLE}>I</span>
                    </Box>
                </MenuItem>
                <MenuItem onClick={handleFullZoomOut} disabled={isRequestInProgress}>
                    <Box sx={MENU_ITEM_ROW_SX}>
                        <Typography variant="body2">Zoom fully OUT</Typography>
                        <span style={KEYBOARD_SHORTCUT_STYLE}>O</span>
                    </Box>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleExitPTZ}>
                    <Typography variant="body2">Exit PTZ mode</Typography>
                </MenuItem>
            </Menu>
        </Tooltip>
    );
}

export default memo(PTZDropdownButton);
